import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertTriangle, Info, CheckCircle, AlertCircle } from 'lucide-react';

export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  type?: 'danger' | 'warning' | 'info' | 'success';
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  showCloseButton?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = 'warning',
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  isLoading = false,
  showCloseButton = true
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    if (!isLoading) {
      setIsVisible(false);
      setTimeout(onClose, 200);
    }
  };

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      handleClose();
    }
  };

  const getIconAndColors = () => {
    switch (type) {
      case 'danger':
        return {
          icon: <AlertCircle className="w-6 h-6 text-red-500" />,
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          confirmBg: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          confirmText: 'text-white'
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="w-6 h-6 text-yellow-500" />,
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          confirmBg: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
          confirmText: 'text-white'
        };
      case 'info':
        return {
          icon: <Info className="w-6 h-6 text-blue-500" />,
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          confirmBg: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
          confirmText: 'text-white'
        };
      case 'success':
        return {
          icon: <CheckCircle className="w-6 h-6 text-green-500" />,
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          confirmBg: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
          confirmText: 'text-white'
        };
      default:
        return {
          icon: <Info className="w-6 h-6 text-gray-500" />,
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          confirmBg: 'bg-gray-600 hover:bg-gray-700 focus:ring-gray-500',
          confirmText: 'text-white'
        };
    }
  };

  const { icon, bgColor, borderColor, confirmBg, confirmText: confirmButtonText } = getIconAndColors();

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[70] overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="flex items-end sm:items-center justify-center min-h-full px-0 sm:px-4 pt-4 pb-0 sm:pb-20">
        {/* Backdrop */}
        <div 
          className={`fixed inset-0 transition-opacity duration-300 ${
            isVisible ? 'bg-black bg-opacity-50' : 'bg-black bg-opacity-0'
          }`}
        />

        {/* Modal */}
        <div 
          className={`relative z-10 inline-block w-full max-w-md px-5 sm:px-6 pt-6 pb-6 sm:my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-t-2xl sm:rounded-lg border ${borderColor} ${
            isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`flex-shrink-0 p-2 rounded-full ${bgColor} mr-3`}>
                {icon}
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                {title}
              </h3>
            </div>
            {showCloseButton && (
              <button
                onClick={handleClose}
                disabled={isLoading}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="mt-4">
            <div className="text-sm text-gray-600 whitespace-pre-line">
              {message}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isLoading}
              className={`w-full sm:w-auto px-4 py-2 text-sm font-medium ${confirmButtonText} border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${confirmBg}`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Processando...
                </div>
              ) : (
                confirmText
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook para facilitar o uso do modal
export const useConfirmModal = () => {
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'info' | 'success';
    confirmText?: string;
    cancelText?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning'
  });

  const [isLoading, setIsLoading] = useState(false);

  const showConfirm = useCallback((
    title: string,
    message: string,
    options: {
      type?: 'danger' | 'warning' | 'info' | 'success';
      confirmText?: string;
      cancelText?: string;
      onConfirm?: () => void | Promise<void>;
      onCancel?: () => void;
    } = {}
  ) => {
    return new Promise<boolean>((resolve) => {
      setModalState({
        isOpen: true,
        title,
        message,
        type: options.type || 'warning',
        confirmText: options.confirmText || 'Confirmar',
        cancelText: options.cancelText || 'Cancelar',
        onConfirm: async () => {
          if (options.onConfirm) {
            setIsLoading(true);
            try {
              await options.onConfirm();
              setModalState(prev => ({ ...prev, isOpen: false }));
              resolve(true);
            } catch (error) {
              console.error('Erro na confirmação:', error);
            } finally {
              setIsLoading(false);
            }
          } else {
            setModalState(prev => ({ ...prev, isOpen: false }));
            resolve(true);
          }
        },
        onCancel: () => {
          if (options.onCancel) {
            options.onCancel();
          }
          setModalState(prev => ({ ...prev, isOpen: false }));
          resolve(false);
        }
      });
    });
  }, []);

  const hideModal = useCallback(() => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const ConfirmModalComponent = React.useCallback(() => (
    <ConfirmModal
      isOpen={modalState.isOpen}
      onClose={hideModal}
      onConfirm={modalState.onConfirm || hideModal}
      title={modalState.title}
      message={modalState.message}
      type={modalState.type}
      confirmText={modalState.confirmText}
      cancelText={modalState.cancelText}
      isLoading={isLoading}
    />
  ), [modalState, isLoading, hideModal]);

  return {
    showConfirm,
    hideModal,
    ConfirmModalComponent
  };
};

export default ConfirmModal;
